import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/enterprise-public-media-server',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_media_enterprise-media-categories';

/**
 * Creates a category under the account's master category. Only one level of categories is supported under the master category. Before creating categories, ensure [Enterprise Onboarding](#enterpriseonboarding) has been called to initialize the account's root category structure.
 *
 * To delete a category, call [Delete Category](#deletecategory).
 */
export function createCategory(payload: object): RequestOptionsFactory<any> {
  function __createCategory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'category.sortOrder' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'category.createdDate' },
          { path: 'category.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_category',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.CreateCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
        { protoPath: '/v1/enterprise/categories', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'category.sortOrder' }],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.createdDate' },
              { path: 'category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCategory;
}

/**
 * Deletes a category including all its subcategories. Items in the category are not deleted.
 *
 * To create a category, call [Create Category](#createcategory).
 */
export function deleteCategory(payload: object): RequestOptionsFactory<any> {
  function __deleteCategory({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_category',
      method: 'DELETE' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.DeleteCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
        {
          protoPath: '/v1/enterprise/categories/{categoryId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCategory;
}

/** Updates a category. */
export function updateCategory(payload: object): RequestOptionsFactory<any> {
  function __updateCategory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'category.sortOrder' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'category.createdDate' },
          { path: 'category.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_category',
      method: 'PATCH' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.UpdateCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
        {
          protoPath: '/v1/enterprise/categories/{category.id}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'category.sortOrder' }],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.createdDate' },
              { path: 'category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCategory;
}

/** Retrieves a category by ID, optionally including subcategories. */
export function getCategory(payload: object): RequestOptionsFactory<any> {
  function __getCategory({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_category',
      method: 'GET' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.GetCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
        {
          protoPath: '/v1/enterprise/categories/{categoryId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'category.category.sortOrder' }],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.category.createdDate' },
              { path: 'category.category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCategory;
}

/**
 * Initializes the enterprise category structure for an organization during first-time setup. Creates the root account category using the provided account information.
 * If the root account category already exists, this method returns the existing category structure without creating duplicates.
 *
 * The caller's account ID must match the `accountId` in the request.
 */
export function enterpriseOnboarding(
  payload: object
): RequestOptionsFactory<any> {
  function __enterpriseOnboarding({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_category',
      method: 'POST' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.EnterpriseOnboarding',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
        { protoPath: '/v1/enterprise/onboarding', data: payload, host }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'category.sortOrder' }],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.createdDate' },
              { path: 'category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __enterpriseOnboarding;
}

/** Retrieves the category tree for the media manager, including all categories accessible to the current user. */
export function getMediaManagerCategories(
  payload: object
): RequestOptionsFactory<any> {
  function __getMediaManagerCategories({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.media.enterprise_public_media.v1.enterprise_category',
      method: 'GET' as any,
      methodFqn:
        'com.wix.media.enterprise_public_media.v1.EnterpriseCategoriesService.GetMediaManagerCategories',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixMediaEnterprisePublicMediaV1EnterpriseCategoriesServiceUrl(
        {
          protoPath: '/v1/enterprise/media-manager/categories',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'category.category.sortOrder' }],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'category.category.createdDate' },
              { path: 'category.category.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getMediaManagerCategories;
}
